import classcat from "classcat";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@touchtech/web-baselayer-ui";

import CircularThumbnails from "@is/components/CircularThumbnails";
import { TRANSLATE_X } from "@is/components/CircularThumbnails/CircularThumbnails.constants";
import { TranslationContext } from "@is/constants";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";

import css from "./ParkedBasketItem.module.css";

type Props = {
  basketId: string;
  basketName: string;
  deviceName: string;
  quantity: string;
  total: string;
  expiration: string;
  thumbnails: string[];
  onResumeClick: (id: string, sessionName: string) => void;
};

const CIRCULAR_THUMBNAILS_INTERMEDIATE_LIMIT = 6;
const CIRCULAR_THUMBNAILS_LIMIT = 5;

const ParkedBasketItem = ({
  basketId,
  basketName,
  deviceName,
  expiration,
  quantity,
  total,
  thumbnails,
  onResumeClick,
}: Props) => {
  const { t } = useTranslation(TranslationContext.Settings);

  const handleClick = useCallback(() => {
    onResumeClick(basketId, basketName);

    trackEvent({
      eventType: TrackingEventTypes.RestoreParkedBasket,
      data: { basketName, quantity, total },
    });
  }, [basketId, basketName, onResumeClick, quantity, total]);

  const thumbnailsToShow = useMemo(() => {
    if (
      CIRCULAR_THUMBNAILS_INTERMEDIATE_LIMIT &&
      thumbnails.length === CIRCULAR_THUMBNAILS_INTERMEDIATE_LIMIT
    ) {
      return thumbnails;
    }

    if (thumbnails.length <= CIRCULAR_THUMBNAILS_LIMIT) {
      return thumbnails;
    }

    return thumbnails.slice(0, CIRCULAR_THUMBNAILS_LIMIT);
  }, [thumbnails]);

  return (
    <div className={css.container}>
      <div className={css.basketInfoContainer}>
        <div className={css.basketNameContainer}>
          <span
            className={classcat([
              css.basketName,
              css.ellipsis,
              "text-label-lg",
            ])}
          >
            {basketName}
          </span>
          <span className={classcat([css.deviceName, css.ellipsis])}>
            {deviceName}
          </span>
        </div>
        <div className={css.basketInfo}>
          <span className={classcat([css.quantity, css.ellipsis])}>
            {quantity}
          </span>
          <span className={classcat([css.total, css.ellipsis])}>{total}</span>
        </div>
        <div
          className={css.thumbnailContainer}
          style={{
            transform: `translateX(-${
              thumbnailsToShow.length * TRANSLATE_X
            }px)`,
          }}
        >
          <CircularThumbnails
            thumbnails={thumbnails}
            totalItems={thumbnails.length}
            intermediateLimit={CIRCULAR_THUMBNAILS_INTERMEDIATE_LIMIT}
          />
        </div>
      </div>
      <div className={css.actionsContainer}>
        <span className={css.expiration}>{expiration}</span>
        <Button variant="secondary" sizing="sm" onClick={handleClick}>
          {t("Resume")}
        </Button>
      </div>
    </div>
  );
};

export default ParkedBasketItem;
